import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { FloatingWhatsAppButton } from '@/components/FloatingWhatsAppButton';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = 'loading' | 'valid' | 'already' | 'invalid' | 'submitting' | 'success' | 'error';

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState<State>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } },
        );
        const body = await res.json();
        if (!res.ok) {
          setState('invalid');
          return;
        }
        if (body.valid === false && body.reason === 'already_unsubscribed') {
          setState('already');
        } else if (body.valid === true) {
          setState('valid');
        } else {
          setState('invalid');
        }
      } catch {
        setState('invalid');
      }
    })();
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setState('submitting');
    try {
      const { data, error } = await supabase.functions.invoke('handle-email-unsubscribe', { body: { token } });
      if (error) throw error;
      if ((data as any)?.success) setState('success');
      else if ((data as any)?.reason === 'already_unsubscribed') setState('already');
      else {
        setErrorMsg((data as any)?.error || 'Error');
        setState('error');
      }
    } catch (e: any) {
      setErrorMsg(e.message);
      setState('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Cancelar suscripción</CardTitle>
          <CardDescription>Gestiona los correos que recibes de LIDER IA</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === 'loading' && (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Validando…</div>
          )}
          {state === 'invalid' && (
            <div className="flex items-start gap-2 text-destructive"><AlertCircle className="w-5 h-5 mt-0.5" /><p>Enlace inválido o expirado.</p></div>
          )}
          {state === 'already' && (
            <div className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-500" /><p>Ya estás dado de baja de nuestros correos.</p></div>
          )}
          {state === 'valid' && (
            <>
              <p className="text-sm text-muted-foreground">¿Confirmas que deseas dejar de recibir correos?</p>
              <Button onClick={handleConfirm} className="w-full">Confirmar baja</Button>
            </>
          )}
          {state === 'submitting' && (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Procesando…</div>
          )}
          {state === 'success' && (
            <div className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-500" /><p>Te hemos dado de baja. No recibirás más correos.</p></div>
          )}
          {state === 'error' && (
            <div className="flex items-start gap-2 text-destructive"><AlertCircle className="w-5 h-5 mt-0.5" /><p>{errorMsg || 'Ocurrió un error.'}</p></div>
          )}
        </CardContent>
      </Card>
      <FloatingWhatsAppButton />
    </div>
  );
}
